// Refetch this far before real expiry so a token cannot die mid-handshake.
const EXPIRY_MARGIN_MS = 60_000;

let cached: { jwt: string; expiresAt: number } | null = null;
let inFlight: Promise<string> | null = null;

/**
 * Thrown when minting a stream token fails. Carries the HTTP status so a
 * caller can tell a dead session (401/403) apart from a transient error.
 */
export class StreamTokenError extends Error {
  status: number;

  constructor(status: number) {
    super(`stream token request failed: ${status}`);
    this.name = 'StreamTokenError';
    this.status = status;
  }
}

/**
 * Clears the module-level token cache. Used in tests between cases, and at
 * logout so a stale scoped token never outlives a sign-out.
 */
export const clearStreamTokenCache = () => {
  cached = null;
  inFlight = null;
};

/**
 * Get a short-lived token scoped to streaming endpoints.
 *
 * Streaming calls go direct to the backend, so they need a credential in a
 * header rather than the session cookie. The token is cached in memory only,
 * never in web storage, and is refetched shortly before it expires. Concurrent
 * callers share one in-flight request.
 */
export async function getStreamToken(): Promise<string> {
  if (cached && cached.expiresAt - Date.now() > EXPIRY_MARGIN_MS) {
    return cached.jwt;
  }

  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const response = await fetch('/api/be/stream-token', { method: 'POST' });
      if (!response.ok) throw new StreamTokenError(response.status);

      const data = await response.json();
      cached = { jwt: data.jwt, expiresAt: Date.now() + data.expiresIn * 1000 };
      return data.jwt;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}
